/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, ElementRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import * as fromSharedStore from 'app/shared/store';
import {
  GoogleAnalyticsService,
  LoadingBarService,
  ModalService,
  PurchaseService,
  ScriptLoaderService,
  WebsocketsService,
} from 'app/shared/services';

import defaultLanguage from './../assets/i18n/en.json';

// add plugins to dayjs
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';

dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(duration);

declare let ga: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  @ViewChild('modalContainer') modalContainer: ElementRef;

  title = 'NextPro';
  message = '';
  messageType = '';

  sliderTemplate =
    '<div class="slider"><div class="line"></div><div class="subline inc"></div><div class="subline dec"></div></div>';

  constructor(
    private modalService: ModalService,
    private router: Router,
    private store: Store<fromSharedStore.SharedState>,
    private translate: TranslateService,
    private purchaseService: PurchaseService,
    private loadingBarService: LoadingBarService,
    private gaService: GoogleAnalyticsService,
    private scriptLoaderService: ScriptLoaderService,
    private websocketsService: WebsocketsService
  ) {
    this.initTranslation();

    this.handleRouteNavigation();

    this.loadCartData();

    this.loadAdditionalCss();

    this.websocketsService.connect();
  }

  ngAfterViewInit() {
    this.modalService.init(this.modalContainer);
  }

  closeSystemNotification() {
    this.store.dispatch(new fromSharedStore.ClearSystemNotification());
  }

  removeModal() {
    this.modalService.hideModal();
  }

  private handleRouteNavigation() {
    let isFirstNavigation = true;

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((ev: NavigationEnd) => {
      this.loadingBarService.hide();

      // if this is the first load of the app then wait for 3 seconds
      // so that the authentication request is done.
      // We are logging the login state also.
      const navTimeout = isFirstNavigation ? 3000 : 0;
      isFirstNavigation = false;
      setTimeout(() => {
        this.gaService.sendPageview(ev.urlAfterRedirects);
      }, navTimeout);
    });

    // when navigating between pages show the loading bar
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.loadingBarService.show();
    });
    // if navigation was cancelled then hide the loading bar
    this.router.events.pipe(filter((event) => event instanceof NavigationCancel)).subscribe(() => {
      this.loadingBarService.hide();
    });
  }

  private initTranslation() {
    this.translate.setTranslation('en', defaultLanguage);
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    // this.translate.use('en');
  }

  private loadCartData() {
    const savedPlan = this.purchaseService.getPlan();
    this.store.dispatch(new fromSharedStore.SetPlan(savedPlan));
  }

  private async loadAdditionalCss() {
    // run when idle
    setTimeout(() => {
      this.scriptLoaderService.loadScript(ScriptLoaderService.SCRIPTS.BITMOVIN_CSS).subscribe();
    }, 500);
  }
}
